/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useTheme } from '@emotion/react'

import { Flex } from '@twisto/components/atoms/flex'
import type { IconComponent } from '@twisto/icons'
import { colorUtils } from '@twisto/styles'

import * as styles from './circle-loader.styles'

type Props = {
  icon?: IconComponent
  size?: number
  startColor?: string
  endColor?: string
  className?: string
}

const initialWidth = 128
const strokeWidth = 4

export const CircleLoader = ({
  icon,
  size = initialWidth,
  startColor,
  endColor,
  className,
}: Props) => {
  const Icon = icon
  const iconSize = Math.floor(size / 2)
  const radius = size / 2 - strokeWidth
  const pathLength = Math.PI * radius * 2

  const theme = useTheme()
  const computedStartColor =
    startColor ?? colorUtils.colorWithOpacity(theme.palette.brand[500], 0)
  const computedEndColor = endColor ?? theme.palette.brand[500]

  return (
    <Flex
      alignItems="center"
      className={className}
      css={styles.root(size)}
      data-testid="circle-loader"
      justify="center"
    >
      <div css={styles.loader}>
        <svg height={size} width={size}>
          <defs>
            <linearGradient id="gradient" x1="0%" x2="0%" y1="0%" y2="100%">
              <stop offset="0%" stopColor={computedStartColor} />
              <stop offset="100%" stopColor={computedEndColor} />
            </linearGradient>
          </defs>
          <circle
            css={styles.circleBackground}
            cx="50%"
            cy="50%"
            fill="none"
            r={radius}
            strokeWidth={strokeWidth}
          />
          <circle
            css={styles.circle}
            cx="50%"
            cy="50%"
            fill="none"
            r={radius}
            stroke="url(#gradient)"
            strokeDasharray={pathLength / 2}
            strokeDashoffset={pathLength / 4}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        </svg>
      </div>
      {Icon && <Icon color="primary" height={iconSize} width={iconSize} />}
    </Flex>
  )
}
